import React, { useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { haversineKm } from "@/components/LocationInput";
import { MapPin, Navigation, Phone, Send, Check, X } from "lucide-react";

const pin = (bg, label) => L.divIcon({
  className: "",
  html: `<div style="background:${bg};color:#0f172a;border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,.35);border-radius:9999px;min-width:26px;height:26px;padding:0 6px;display:flex;align-items:center;justify-content:center;font:700 11px sans-serif">${label}</div>`,
  iconSize: [26, 26], iconAnchor: [13, 13],
});
const PICKUP_ICON = pin("#f59e0b", "P");
const INDIA = [22.5, 82];

const FitBounds = ({ points }) => {
  const map = useMap();
  React.useEffect(() => {
    if (points.length === 1) map.setView(points[0], 11);
    else if (points.length > 1) map.fitBounds(points, { padding: [40, 40], maxZoom: 12 });
  }, [JSON.stringify(points)]);
  return null;
};

export const DriverMap = ({ drivers, leads, onChanged }) => {
  const [leadId, setLeadId] = useState("");
  const located = drivers.filter((d) => d.lat != null && d.lng != null);
  const openLeads = leads.filter((l) => l.from_lat != null && l.from_lng != null && !l.accepted_by);
  const lead = openLeads.find((l) => l.id === leadId);
  const pickup = lead ? { lat: lead.from_lat, lng: lead.from_lng } : null;
  const ranked = useMemo(() => located
    .map((d) => ({ ...d, km: pickup ? haversineKm(pickup, d) : null }))
    .sort((a, b) => (a.km ?? 0) - (b.km ?? 0)), [located, pickup]);
  const points = [...located.map((d) => [d.lat, d.lng]), ...(pickup ? [[pickup.lat, pickup.lng]] : [])];

  return (
    <div data-testid="admin-driver-map" className="lg:col-span-5 min-w-0 bg-white rounded-2xl border border-slate-200/80 p-5 shadow-[0_2px_8px_rgba(15,23,42,0.06)]">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <h3 className="font-display font-bold text-slate-900 flex items-center gap-2"><Navigation className="w-4 h-4 text-amber-500" /> Live Driver Map</h3>
        <select data-testid="admin-map-lead-select" value={leadId} onChange={(e) => setLeadId(e.target.value)}
          className="h-9 w-full sm:w-auto min-w-0 rounded-lg border border-slate-200 px-2 text-sm max-w-full sm:max-w-sm">
          <option value="">Pick an enquiry to find nearest drivers</option>
          {openLeads.map((l) => <option key={l.id} value={l.id}>{l.from_location} → {l.to_location} · {l.vehicle_type}</option>)}
        </select>
      </div>
      <div className="mt-4 grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 h-80 sm:h-96 rounded-xl overflow-hidden border border-slate-200 relative z-0">
          <MapContainer center={INDIA} zoom={5} scrollWheelZoom className="w-full h-full">
            <TileLayer attribution='&copy; OpenStreetMap contributors' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
            <FitBounds points={points} />
            {ranked.map((d, i) => (
              <Marker key={d.user_id} position={[d.lat, d.lng]} icon={pin("#10b981", pickup ? i + 1 : "D")}>
                <Popup><b>{d.name}</b><br />{d.vehicle_type} · {d.phone}{d.km != null ? <><br />{d.km.toFixed(1)} km from pickup</> : null}</Popup>
              </Marker>
            ))}
            {pickup && (
              <Marker position={[pickup.lat, pickup.lng]} icon={PICKUP_ICON}><Popup><b>Pickup</b><br />{lead.from_location}</Popup></Marker>
            )}
            {pickup && ranked[0] && <Polyline positions={[[pickup.lat, pickup.lng], [ranked[0].lat, ranked[0].lng]]} pathOptions={{ color: "#f59e0b", dashArray: "6 6" }} />}
          </MapContainer>
        </div>
        <NearestList ranked={ranked} lead={lead} onChanged={onChanged} />
      </div>
    </div>
  );
};

const NearestList = ({ ranked, lead, onChanged }) => {
  const [busy, setBusy] = useState(null);
  const [sentMap, setSentMap] = useState({});
  const sent = lead ? (sentMap[lead.id] ?? (lead.targeted ? lead.assigned_drivers || [] : [])) : [];
  const toggle = async (d) => {
    const isSent = sent.includes(d.user_id);
    const force = !isSent && lead.verified === false;
    if (force && !window.confirm("This customer has not verified yet. Send anyway?")) return;
    setBusy(d.user_id);
    try {
      const url = `/admin/leads/${lead.id}/assign/${d.user_id}`;
      const r = isSent ? await api.delete(url) : await api.post(url, { force });
      setSentMap((m) => ({ ...m, [lead.id]: r.data.assigned_drivers }));
      toast.success(isSent ? `Removed ${d.name}` : `Sent to ${d.name}`);
      if (onChanged) onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send"); }
    finally { setBusy(null); }
  };
  return (
    <div data-testid="admin-nearest-drivers" className="max-h-96 overflow-y-auto space-y-2">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm font-semibold text-slate-700">{lead ? "Nearest to pickup" : `${ranked.length} driver(s) on map`}</p>
        {lead && <span data-testid="admin-assigned-count" className="text-xs font-semibold rounded-full bg-amber-100 text-amber-700 px-2 py-0.5">{sent.length}/5 sent</span>}
      </div>
      {lead && lead.lead_price == null && <p data-testid="admin-assign-price-hint" className="text-xs text-amber-600">Set a lead price in the Leads tab before sending.</p>}
      {ranked.length === 0 && <p className="text-sm text-slate-400">No online drivers have shared location yet</p>}
      {ranked.map((d, i) => {
        const isSent = sent.includes(d.user_id);
        return (
          <div key={d.user_id} data-testid="admin-nearest-driver-row" className={`rounded-xl border px-3 py-2 ${isSent ? "border-emerald-200 bg-emerald-50/50" : "border-slate-100"}`}>
            <div className="flex justify-between gap-2">
              <p className="text-sm font-semibold text-slate-900 truncate">{lead ? `${i + 1}. ` : ""}{d.name}</p>
              {d.km != null && <span data-testid="admin-nearest-driver-distance" className="text-xs font-bold text-amber-600 shrink-0 flex items-center gap-0.5"><MapPin className="w-3 h-3" />{d.km.toFixed(1)} km</span>}
            </div>
            <div className="flex items-center justify-between gap-2">
              <p className="text-xs text-slate-500 min-w-0 truncate">
                {d.vehicle_type || "—"}{lead && d.vehicle_type === lead.vehicle_type ? " · vehicle match" : ""}
                {d.phone && <a href={`tel:+91${d.phone}`} className="ml-2 inline-flex items-center gap-0.5 hover:text-amber-600"><Phone className="w-3 h-3" />{d.phone}</a>}
              </p>
              {lead && (
                <button data-testid={`admin-assign-driver-${d.user_id}`} onClick={() => toggle(d)}
                  disabled={busy === d.user_id || lead.lead_price == null || (!isSent && sent.length >= 5)}
                  title={isSent ? "Remove this driver" : "Send this enquiry to this driver only"}
                  className={`shrink-0 h-7 px-2.5 rounded-lg text-xs font-bold flex items-center gap-1 transition-colors disabled:opacity-40 ${isSent ? "bg-emerald-600 text-white hover:bg-red-600" : "bg-slate-900 text-white hover:bg-slate-700"}`}>
                  {isSent ? <><Check className="w-3 h-3" /> Sent <X className="w-3 h-3" /></> : <><Send className="w-3 h-3" /> Send</>}
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};
